// Shared lifecycle for Portal-mounted modal dialogs:
//   - document-level Escape closes the modal
//   - on unmount, focus is returned to whatever element was focused
//     when the dialog opened (so the user lands back on the trigger
//     button that opened it)
//
// The Escape listener is gated by an optional `isOpen` accessor so an
// always-mounted modal (e.g. the global voyage-details popup or the
// date-event dialog provider) only swallows Escape while it's visible.
// When the modal is conditionally rendered (mounted ⇒ visible),
// `isOpen` can be omitted and the hook fires Escape unconditionally.
//
// Callers that need other keys (Enter-to-save, Ctrl+V passthrough)
// keep their own keydown listener — this hook deliberately only
// handles Escape so the contract stays narrow.

import { onCleanup, onMount, type Accessor } from "solid-js";

export interface UseModalDismissOpts {
  /** Called when Escape fires while the modal is open. */
  onClose: () => void;
  /** Optional gate. Defaults to always-open. */
  isOpen?: Accessor<boolean> | undefined;
  /** When true, captures document.activeElement on construction and
   *  refocuses it on cleanup if it's still in the DOM. */
  restoreFocus?: boolean | undefined;
}

export function useModalDismiss(opts: UseModalDismissOpts): void {
  // Capture the previously-focused element at hook construction time,
  // matching the pattern existing dialogs use (and runs before any
  // queueMicrotask focus into the dialog itself).
  const previouslyFocused =
    opts.restoreFocus && typeof document !== "undefined"
      ? (document.activeElement as HTMLElement | null)
      : null;

  function onKey(e: KeyboardEvent) {
    if (e.key !== "Escape") return;
    if (opts.isOpen && !opts.isOpen()) return;
    opts.onClose();
  }

  onMount(() => {
    document.addEventListener("keydown", onKey);
  });
  onCleanup(() => {
    document.removeEventListener("keydown", onKey);
    if (
      opts.restoreFocus &&
      previouslyFocused &&
      typeof document !== "undefined" &&
      document.body.contains(previouslyFocused)
    ) {
      previouslyFocused.focus();
    }
  });
}
